import pako from "pako";
import { cache } from "@cornerstonejs/core";
import { segmentation } from "@cornerstonejs/tools";

export interface CompressedSegmentationData {
  dimensionsX: number;
  dimensionsY: number;
  dimensionsZ: number;
  spacingX: number;
  spacingY: number;
  spacingZ: number;
  origin: number[];
  direction: number[];
  compressedData: string; // Base64 encoded gzip data
  compressionType: "gzip";
  uncompressedSize: number;
  compressedSize: number;
}

/**
 * Compress labelmap data for storage
 */
export const compressLabelmapData = (
  segmentationId: string
): CompressedSegmentationData | null => {
  try {
    const volume = cache.getVolume(segmentationId);
    if (!volume) {
      console.error("Volume not found:", segmentationId);
      return null;
    }

    const [dimensionsX, dimensionsY, dimensionsZ] = volume.dimensions;
    const [spacingX, spacingY, spacingZ] = volume.spacing;

    // Get the raw scalar data
    const voxelManager = volume.voxelManager;
    const scalarData = voxelManager.getCompleteScalarDataArray();

    // Compress using gzip
    const compressed = pako.gzip(scalarData);

    // Convert to base64 for storage. Build the binary string byte-by-byte —
    // spread or `apply` would overflow the call stack on real drawings, since
    // the compressed payload routinely runs hundreds of KB.
    let binary = "";
    for (let i = 0; i < compressed.length; i++) {
      binary += String.fromCharCode(compressed[i]);
    }
    const base64Data = btoa(binary);

    const result: CompressedSegmentationData = {
      dimensionsX,
      dimensionsY,
      dimensionsZ,
      spacingX,
      spacingY,
      spacingZ,
      origin: Array.from(volume.origin),
      direction: Array.from(volume.direction),
      compressedData: base64Data,
      compressionType: "gzip",
      uncompressedSize: scalarData.length,
      compressedSize: compressed.length,
    };

    console.log(
      `Compressed labelmap: ${scalarData.length} bytes → ${
        compressed.length
      } bytes (${((compressed.length / scalarData.length) * 100).toFixed(1)}%)`
    );

    return result;
  } catch (error) {
    console.error("Error compressing labelmap:", error);
    return null;
  }
};

/**
 * Decompress and load labelmap data into volume
 */
export const decompressAndLoadLabelmap = (
  segmentationId: string,
  compressedData: CompressedSegmentationData
): boolean => {
  try {
    const volume = cache.getVolume(segmentationId);
    if (!volume) {
      console.error("Volume not found:", segmentationId);
      return false;
    }

    // Decode base64
    const binaryString = atob(compressedData.compressedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decompress
    const decompressed = pako.ungzip(bytes);

    // Verify decompressed data matches stored dimensions
    const savedSize =
      compressedData.dimensionsX *
      compressedData.dimensionsY *
      compressedData.dimensionsZ;
    if (decompressed.length !== savedSize) {
      console.error(
        "Decompressed data size mismatch:",
        decompressed.length,
        "expected:",
        savedSize
      );
      return false;
    }

    const voxelManager = volume.voxelManager;
    const [volW, volH, volD] = volume.dimensions;
    const savedW = compressedData.dimensionsX;
    const savedH = compressedData.dimensionsY;
    const savedD = compressedData.dimensionsZ;

    if (savedW === volW && savedH === volH && savedD === volD) {
      // Dimensions match — direct copy
      for (let i = 0; i < decompressed.length; i++) {
        voxelManager.setAtIndex(i, decompressed[i]);
      }
    } else {
      // Dimensions differ — nearest-neighbor resample into the volume
      console.log(
        `Resampling saved data ${savedW}x${savedH}x${savedD} → ${volW}x${volH}x${volD}`
      );
      const scaleX = savedW / volW;
      const scaleY = savedH / volH;
      const scaleZ = savedD / volD;
      for (let z = 0; z < volD; z++) {
        const srcZ = Math.min(Math.floor(z * scaleZ), savedD - 1);
        for (let y = 0; y < volH; y++) {
          const srcY = Math.min(Math.floor(y * scaleY), savedH - 1);
          for (let x = 0; x < volW; x++) {
            const srcX = Math.min(Math.floor(x * scaleX), savedW - 1);
            const srcIdx = srcZ * savedW * savedH + srcY * savedW + srcX;
            const dstIdx = z * volW * volH + y * volW + x;
            if (decompressed[srcIdx] !== 0) {
              voxelManager.setAtIndex(dstIdx, decompressed[srcIdx]);
            }
          }
        }
      }
    }

    console.log(
      `Decompressed labelmap: ${compressedData.compressedSize} bytes → ${decompressed.length} bytes`
    );

    // Wait a moment to ensure segmentation representation is initialized before triggering
    setTimeout(() => {
      try {
        // Verify segmentation exists before triggering event
        const seg = segmentation.state.getSegmentation(segmentationId);
        if (seg) {
          segmentation.triggerSegmentationEvents.triggerSegmentationDataModified(
            segmentationId
          );
          console.log("Triggered segmentation data modified event");
        } else {
          console.warn(
            "Segmentation not found when trying to trigger event:",
            segmentationId
          );
        }
      } catch (error) {
        console.error("Error triggering segmentation event:", error);
      }
    }, 100);

    return true;
  } catch (error) {
    console.error("Error decompressing labelmap:", error);
    return false;
  }
};
